// styles/theme.tsTypeText
import { createTheme, CssVarsThemeOptions, lighten, ThemeOptions } from "@mui/material/styles";
import { Theme } from "@mui/material/styles"; // Theme 타입 임포트
import { red } from "@mui/material/colors";

declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
    videoCard: { background: string; hoverBackground: string };
    customDivider: string;
    buttonHover: string;
    menuItem: { hover: string; selected: string };
    chip: { background: string; hoverBackground: string; text: string };
    text: TypeText;
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions["primary"];
    videoCard?: { background: string; hoverBackground: string };
    customDivider?: string;
    buttonHover?: string;
    menuItem?: { hover: string; selected: string };
    chip?: { background: string; hoverBackground: string; text: string };
    text?: Partial<TypeText>;
  }

  interface TypeText {
    customText?: string;
    commonText?: string;
  }
  interface TypeBackground {
    customBg?: string;
  }
}

type TCommonTheme = Omit<ThemeOptions, "components"> &
  Pick<CssVarsThemeOptions, "defaultColorScheme" | "colorSchemes" | "components"> & {
    cssVariables?:
      | boolean
      | Pick<
          CssVarsThemeOptions,
          "colorSchemeSelector" | "rootSelector" | "disableCssColorScheme" | "cssVarPrefix" | "shouldSkipGeneratingVar"
        >;
  };

// 공통 테마 정의 (components 포함, Theme 타입 지정)
export const commonTheme: TCommonTheme = {
  cssVariables: {
    colorSchemeSelector: "data",
  },
  breakpoints: {
    //mui 기본 사이즈
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily:
      "var(--font-kr),var(--font-sans),var(--font-jp),var(--font-tc),var(--font-sc),var(--font-arabic),var(--font-thai),var(--font-lao),var(--font-kr),Roboto,Arial,sans-serif",
    h1: { fontSize: "26px", fontWeight: 600, lineHeight: 1.5 },
    h2: { fontSize: "24px", fontWeight: 600, lineHeight: 1.5 },
    h3: { fontSize: "20px", fontWeight: 600, lineHeight: 1.5 },
    h4: { fontSize: "18px", fontWeight: 600, lineHeight: 1.5 },
    h5: { fontSize: "16px", fontWeight: 600, lineHeight: 1.5 },
    h6: { fontSize: "14px", fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: "13px", lineHeight: 1.5 },
    body2: { fontSize: "12px", lineHeight: 1.5 },
    caption: { fontSize: "12px", lineHeight: 1.5 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundImage: "none",
          backgroundColor: theme.palette.background.default,
          boxShadow: "none",
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundImage: "none",
          backgroundColor: theme.palette.background.default,
          boxShadow: "none",
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: "xl", // MUI 기본 maxWidth 비활성화
      },
      styleOverrides: {
        root: {
          //width: "100% !important", // 100% 설정
          //maxWidth: "100% !important", // MUI 기본 제한 제거
          boxShadow: "none",
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          flexGrow: 1, // 각 액션이 가능한 공간을 차지
          transition: "none", // 불필요한 트랜지션 제거
          WebkitTapHighlightColor: "transparent", // 터치 반응 속도 개선
          "& .MuiTouchRipple-root": {
            animationDuration: "100ms", // Ripple 속도 개선
          },
        }),
        label: {
          whiteSpace: "nowrap", // 줄 바꿈 금지
          overflow: "hidden",
          textOverflow: "ellipsis", // 넘칠 때 말줄임
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          textTransform: "none",
          borderRadius: "18px",
          padding: "6px 12px",
          fontWeight: 500,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.secondary,
        }),
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        text: {
          "&:hover": {
            backgroundColor: ({ theme }: { theme: Theme }) => theme.palette.buttonHover,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: 8,
          color: theme.palette.text.primary,
          // "&:hover": { backgroundColor: theme.palette.buttonHover },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          height: "48px",
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "16px", // 최소 16px 유지하여 확대 방지
          touchAction: "manipulation", // 터치 확대 방지
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputProps: {
          style: { fontSize: "16px" },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: 0,
          boxShadow: "none",
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: "8px",
          height: "32px",
          backgroundColor: theme.palette.chip.background,
          color: theme.palette.chip.text,
          "&:hover": { backgroundColor: theme.palette.chip.hoverBackground },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.default,
          boxShadow: "none",
        }),
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: theme.breakpoints.down("xs") ? "98%" : "640px", // sm 이하는 100%, sm 이상은 480px
          margin: "0 auto", // 가운데 정렬
          borderRadius: "8px",
          fontSize: "0.95rem",
          padding: "6px 12px",
          backgroundColor: theme.palette.mode === "dark" ? "#424242" : "#F5F5F5", // 어두운 회색 / 밝은 회색
        }),
        standardSuccess: ({ theme }) => ({
          color: theme.palette.mode === "dark" ? "#E0E0E0 " : "#333333 ", // 다크/라이트 모드에 맞는 녹색
        }),
        standardError: ({ theme }) => ({
          color: theme.palette.mode === "dark" ? "#F44336" : "#D32F2F", // 다크/라이트 모드에 맞는 빨간색
        }),
        standardWarning: ({ theme }) => ({
          color: theme.palette.mode === "dark" ? "#FFB74D" : "#F57C00", // 다크/라이트 모드에 맞는 오렌지색
        }),
        standardInfo: ({ theme }) => ({
          color: theme.palette.mode === "dark" ? "#29B6F6" : "#1976D2", // 다크/라이트 모드에 맞는 파란색
        }),
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => {
          const defaultBg = theme.palette.background.default;
          const lighterBg = lighten(defaultBg, 0.2); // 기본색보다 20% 밝게

          return {
            backgroundColor: lighterBg, // 기본 배경을 조금 더 밝게 설정
            transition: "background-color 0.3s ease",
            "&:hover": {
              backgroundColor: lighten(defaultBg, 0.4), // hover 시 더 밝게
            },
          };
        },
      },
    },
  },
};

// Light 테마
export const lightTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: "light",
    primary: {
      main: "#0F0F0F",
      light: "#606060",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#1E9E23",
      light: "#64BF55",
      dark: "#1F7A13",
      contrastText: "#FFFFFF",
    },
    tertiary: {
      main: "#F01616",
      light: "#F44336",
      dark: "#C90707",
      contrastText: "#FFFFFF",
    },
    background: { default: "#FFFFFF", paper: "#F5F5F5", customBg: "#E8EFEA" },
    text: { primary: "#0F0F0F", secondary: "#606060", customText: "#FFFFFF", commonText: "#1E88E5" },
    error: { main: red.A400 },
    divider: "#E5E5E5",
    customDivider: "#E5E5E5",
    buttonHover: "#F2F2F2",
    videoCard: { background: "#FFFFFF", hoverBackground: "#F2F2F2" },
    menuItem: { hover: "#F2F2F2", selected: "#E5E5E5" },
    chip: {
      background: "#F2F2F2",
      hoverBackground: "#E5E5E5",
      text: "#0F0F0F",
    },
  },
});

// Dark 테마
export const darkTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: "dark",
    primary: {
      main: "#FFFFFF",
      light: "#AAAAAA",
      dark: "#FFFFFF",
      contrastText: "#000000",
    },
    secondary: {
      main: "#1E9E23",
      light: "#64BF55",
      dark: "#1F7A13",
      contrastText: "#FFFFFF",
    },
    tertiary: {
      main: "#F01616",
      light: "#F44336",
      dark: "#C90707",
      contrastText: "#000000",
    },
    background: { default: "#1a1a1a", paper: "#25282c", customBg: "#0E0E0E" },
    text: { primary: "#FFFFFF", secondary: "#AAAAAA", customText: "#000000", commonText: "#1E88E5" },
    error: { main: red.A400 },
    divider: "#303030",
    customDivider: "#303030",
    buttonHover: "#FFFFFF",
    videoCard: { background: "#1F1F1F", hoverBackground: "#272727" },
    menuItem: { hover: "#272727", selected: "#383838" },
    chip: {
      background: "#272727",
      hoverBackground: "#383838",
      text: "#FFFFFF",
    },
  },
});
